import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input } from '@amzn/alchemy-components-react';
import { AuthgardAdminAction } from "src/common/enums";
import type { UseBulkManageScan2AuthResult } from "src/hooks/use-bulk-manage-scan2auth";
import { StepFrame } from "src/components/modals/bulk-manage-credentials/step-frame";
import { expandableProps } from "src/components/modals/bulk-manage-credentials/expandable";
import {
    actionLabel,
    DESTRUCTIVE_BUTTON_CLASS,
    ineligibleLabel,
    isDestructiveAction,
    userStatusLabel,
} from "src/components/modals/bulk-manage-credentials/copy";

interface ConfirmStepProps {
    /** The wizard's view model (see SelectActionStep for why it's passed whole). */
    bulk: UseBulkManageScan2AuthResult

    /**
     * The site this run is pinned to. Typed back by the operator to confirm a destructive action —
     * right action against the wrong site is the error this guards, and a count can't catch it.
     */
    scope: string
}

/**
 * Step 2: show the blast radius and take the commit. Names every principal the run will be sent for,
 * and keeps the skipped ones one click away so the numbers reconcile with what the operator ticked.
 * Destructive actions add a warning banner and a type-the-site-code gate before the commit.
 */
export const ConfirmStep = (props: ConfirmStepProps) => {
    const { t } = useTranslation();
    const { scope } = props;
    const {
        selectedAction,
        submitting,
        progress,
        confirmCountInput,
        setConfirmCountInput,
        skippedExpanded,
        setSkippedExpanded,
        handleConfirm,
        handleBack,
        eligibilityFor,
        nowMs,
    } = props.bulk;

    if (!selectedAction) {
        return null;
    }

    const { eligible, ineligible: skipped } = eligibilityFor(selectedAction);
    const destructive = isDestructiveAction(selectedAction);
    // Type-to-confirm for destructive actions: the operator retypes the site code. Typing the affected
    // count was weak friction that got weaker as the stakes fell — for one user it meant typing "1" —
    // and the number was printed beside the input, so it could be copied by eye without being read.
    // The site is the thing worth re-stating: running the right action against the wrong site is a
    // plausible and unrecoverable mistake, and a count cannot catch it. Compared case-insensitively;
    // the field is still named confirmCountInput in the hook, which is CR-302507803's file.
    const confirmEnabled = !destructive
        || confirmCountInput.trim().toLowerCase() === scope.trim().toLowerCase();

    return (
        <StepFrame title={actionLabel(t, selectedAction)}>
            <div>
                {destructive && (
                    <div
                        className="mb-3 p-2"
                        style={{
                            border: '1px solid var(--color-base-error-alert, #c00)',
                            borderRadius: '4px',
                            background: 'var(--color-base-error-notification, #ffdbdb)',
                        }}
                    >
                        <Icon icon="warning" className="mr-1 c-error-alert" />
                        <strong>{t('bulk-manage-credentials-destructive-title')}</strong>{' '}
                        {selectedAction === AuthgardAdminAction.REREGISTER
                            ? t('bulk-manage-credentials-destructive-reset')
                            : t('bulk-manage-credentials-destructive-cancel')}
                    </div>
                )}

                <p className="mb-3">{t('bulk-manage-credentials-affected', { count: eligible.length })}</p>

                <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                    {/* The status must not be compressed and the name must not be clipped: a long name
                        wraps instead, since the operator is confirming which people this runs against.
                        Without mr-2/flex-shrink-0 the two texts butt together with no gap. */}
                    {eligible.map(user => (
                        <div key={user.principalArn} className="d-flex justify-content-between py-1">
                            <span className="mr-2">{user.fullName}</span>
                            <span className="text-muted flex-shrink-0 text-right" style={{ fontSize: '0.85rem' }}>
                                {userStatusLabel(t, user, nowMs)}
                            </span>
                        </div>
                    ))}
                </div>

                {skipped.length > 0 && (
                    <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                        <div
                            className="text-muted"
                            style={{ cursor: 'pointer' }}
                            {...expandableProps(skippedExpanded, () => setSkippedExpanded(prev => !prev))}
                        >
                            <Icon icon={skippedExpanded ? 'caretDown' : 'caretRight'} className="mr-1" />
                            {t('bulk-manage-credentials-skipped-note', { count: skipped.length })}
                        </div>
                        {skippedExpanded && (
                            <div className="mt-1 ml-3" style={{ maxHeight: '20vh', overflowY: 'auto' }}>
                                {skipped.map(({ user, reason }) => (
                                    <div key={user.principalArn} className="d-flex justify-content-between py-1">
                                        <span className="mr-2">{user.fullName}</span>
                                        <span className="text-muted flex-shrink-0 text-right">{ineligibleLabel(t, user, reason, nowMs)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* The prompt goes through Alchemy's own `label` prop, not an outer <label htmlFor>: the
                    id lands on the <alchemy-input> host, so htmlFor can't reach the real <input> inside
                    its shadow root and would leave this gate announced as an unlabelled textbox. */}
                {destructive && (
                    <div className="mt-3">
                        <Input
                            id="bulk-confirm-count-input"
                            className="mt-1"
                            label={selectedAction === AuthgardAdminAction.REREGISTER
                                ? t('bulk-manage-credentials-type-confirm-reset', { count: eligible.length, scope })
                                : t('bulk-manage-credentials-type-confirm-cancel', { count: eligible.length, scope })}
                            onInput={(e: any) => setConfirmCountInput(e.target.value ?? '')}
                        />
                    </div>
                )}

                {/* A cohort larger than one PCS call is sent as consecutive batches, so a big run takes
                    several seconds. Report how far it has got rather than leaving the operator with a
                    disabled button and no signal. */}
                {submitting && progress.total > 0 && (
                    <p className="text-muted mb-0 mt-3" style={{ fontSize: '0.85rem' }}>
                        {t('bulk-manage-credentials-run-progress', {
                            completed: progress.completed,
                            total: progress.total,
                        })}
                    </p>
                )}

                {/* Two buttons, not three. "← Back" returns to action selection; the X and Escape leave
                    the wizard. A "Cancel" alongside them was a third way out for the second of only two
                    ideas, and the select-action step has never had one — so its absence here makes the
                    steps consistent rather than making this one an exception.

                    Back is available only until Confirm commits, then disabled for the run's duration:
                    once targets are dispatched there is nothing to back out of, and leaving this step
                    mid-run would strand the run's outcomes unrendered. */}
                <div className="d-flex justify-content-end mt-3">
                    <Button
                        id="bulk-confirm-back"
                        className="mr-1"
                        size="lg"
                        label={t('bulk-manage-credentials-back')}
                        variant="base"
                        disabled={submitting}
                        onClick={handleBack}
                    />
                    {/* Always the primary: `action` fills it, and a destructive run recolours that fill
                        red rather than dropping to an outline, which would read as LESS prominent than
                        the way back. The label stays "Confirm (n)" either way — restating the action
                        here produced "Cancel Enrollment (1)", which read as a sibling of "Cancel". The
                        step's title, the warning banner and the named list all say which action this is;
                        the count stays because it states the blast radius at the point of commitment. */}
                    <Button
                        id="bulk-confirm-run"
                        size="lg"
                        label={t('bulk-manage-credentials-run', { count: eligible.length })}
                        variant="action"
                        className={destructive ? DESTRUCTIVE_BUTTON_CLASS : undefined}
                        disabled={!confirmEnabled || submitting}
                        onClick={handleConfirm}
                    />
                </div>
            </div>
        </StepFrame>
    );
};
